import { Plus, Trash2, Truck, RotateCcw, Lock, Shield, Award, Package, Star, Zap, CheckCircle, Clock, Gift, Info, XCircle } from "lucide-react";

const BADGE_ICON_OPTIONS = [
  { value: "truck",        label: "Truck (Delivery)" },
  { value: "rotateccw",    label: "Return / Exchange" },
  { value: "xcircle",      label: "X Circle (Non-Returnable)" },
  { value: "lock",         label: "Lock (Secure)" },
  { value: "shield",       label: "Shield (Protection)" },
  { value: "award",        label: "Award (Quality)" },
  { value: "package",      label: "Package (Delivery)" },
  { value: "star",         label: "Star (Rating)" },
  { value: "zap",          label: "Zap (Fast)" },
  { value: "checkcircle",  label: "Check (Verified)" },
  { value: "clock",        label: "Clock (On Time)" },
  { value: "gift",         label: "Gift (Offer)" },
];

const DEFAULT_BADGES = [
  { icon: "truck",       label: "Free\nDelivery",             enabled: true },
  { icon: "rotateccw",   label: "10 days Return\n& Exchange", enabled: true },
  { icon: "package",     label: "Brand\nDelivered",           enabled: true },
  { icon: "lock",        label: "Secure\nTransaction",        enabled: true },
];

function Field({ label, children, className = "" }) {
  return (
    <div className={className}>
      <label className="block text-xs font-medium text-slate-700 mb-1">{label}</label>
      {children}
    </div>
  );
}

const Input = (props) => (
  <input
    {...props}
    className={`w-full px-3 py-2 bg-white border border-slate-300 rounded-lg text-slate-900 placeholder-slate-400 focus:outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20 transition-all text-sm ${props.className || ""}`}
  />
);

const Select = ({ children, ...props }) => (
  <select
    {...props}
    className={`w-full px-3 py-2 bg-white border border-slate-300 rounded-lg text-slate-900 focus:outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20 transition-all text-sm ${props.className || ""}`}
  >
    {children}
  </select>
);

function Section({ title, icon: Icon, children }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl overflow-hidden mb-5">
      <div className="px-5 py-3.5 border-b border-slate-200 flex items-center gap-2">
        {Icon && <Icon className="w-4 h-4 text-orange-500" />}
        <h3 className="text-sm font-semibold text-slate-800">{title}</h3>
      </div>
      <div className="p-5">{children}</div>
    </div>
  );
}

export default function SpecsQATab({ data, onChange }) {
  const sr = data.shipping_returns || {};
  const specs = data.specifications || [];
  const measurements = data.measurements || [];
  const styleDetails = data.style_details || [];
  const qa = data.qa_items || [];

  const updateSR = (field, value) => {
    onChange("shipping_returns", { ...sr, [field]: value });
  };

  const addSpec = () => {
    onChange("specifications", [...specs, { name: "", value: "" }]);
  };
  const updateSpec = (idx, field, value) => {
    onChange("specifications", specs.map((s, i) => i === idx ? { ...s, [field]: value } : s));
  };
  const removeSpec = (idx) => {
    onChange("specifications", specs.filter((_, i) => i !== idx));
  };

  const addMeasurement = () => {
    onChange("measurements", [...measurements, { name: "", value: "" }]);
  };
  const updateMeasurement = (idx, field, value) => {
    onChange("measurements", measurements.map((s, i) => i === idx ? { ...s, [field]: value } : s));
  };
  const removeMeasurement = (idx) => {
    onChange("measurements", measurements.filter((_, i) => i !== idx));
  };

  const addStyleDetail = () => {
    onChange("style_details", [...styleDetails, { name: "", value: "" }]);
  };
  const updateStyleDetail = (idx, field, value) => {
    onChange("style_details", styleDetails.map((s, i) => i === idx ? { ...s, [field]: value } : s));
  };
  const removeStyleDetail = (idx) => {
    onChange("style_details", styleDetails.filter((_, i) => i !== idx));
  };

  const addQA = () => {
    onChange("qa_items", [...qa, { question: "", answer: "" }]);
  };
  const updateQA = (idx, field, value) => {
    onChange("qa_items", qa.map((q, i) => i === idx ? { ...q, [field]: value } : q));
  };
  const removeQA = (idx) => {
    onChange("qa_items", qa.filter((_, i) => i !== idx));
  };

  return (
    <div>
      <div className="flex items-center gap-2 mb-5">
        <div className="w-5 h-5 bg-orange-100 rounded flex items-center justify-center">
          <span className="text-orange-600 text-xs font-bold">8</span>
        </div>
        <h2 className="text-base font-semibold text-slate-900">Specifications &amp; Q&amp;A</h2>
      </div>

      {/* Shipping & Returns */}
      <Section title="Shipping &amp; Returns" icon={Truck}>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Field label="Deliver Within (Days)">
            <Input
              value={sr.deliver_within || ""}
              onChange={e => updateSR("deliver_within", e.target.value)}
              placeholder="2-5"
            />
          </Field>
          <Field label="Ship Within (Days)">
            <Input
              value={sr.ship_within || ""}
              onChange={e => updateSR("ship_within", e.target.value)}
              placeholder="1-2"
            />
          </Field>
          <Field label="Shipping Weight (grams)">
            <Input
              value={sr.shipping_weight || ""}
              onChange={e => updateSR("shipping_weight", e.target.value)}
              placeholder="250"
            />
          </Field>
          <Field label="Shipping Dimension (cm)">
            <Input
              value={sr.shipping_dimension || ""}
              onChange={e => updateSR("shipping_dimension", e.target.value)}
              placeholder="21 x 29.7 x 1"
            />
          </Field>
        </div>
        <div className="mt-4">
          <Field label="Return Policy">
            <Select
              value={sr.return_policy || "Non-returnable"}
              onChange={e => updateSR("return_policy", e.target.value)}
            >
              <option value="Non-returnable">Non-returnable</option>
              <option value="7 days return">7 days return</option>
              <option value="15 days return">15 days return</option>
              <option value="30 days return">30 days return</option>
            </Select>
          </Field>
        </div>
      </Section>

      {/* Specifications */}
      <Section title="Specifications">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200">
                <th className="px-3 py-2 text-left text-xs font-semibold text-slate-500 uppercase">Specification Name</th>
                <th className="px-3 py-2 text-left text-xs font-semibold text-slate-500 uppercase">Specification Value</th>
                <th className="px-3 py-2 w-10"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {specs.map((spec, idx) => (
                <tr key={idx} className="hover:bg-slate-50 transition-colors">
                  <td className="px-3 py-2">
                    <input
                      type="text"
                      value={spec.name}
                      onChange={e => updateSpec(idx, "name", e.target.value)}
                      placeholder="e.g. Brand"
                      className="w-full px-2 py-1.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20"
                    />
                  </td>
                  <td className="px-3 py-2">
                    <input
                      type="text"
                      value={spec.value}
                      onChange={e => updateSpec(idx, "value", e.target.value)}
                      placeholder="e.g. Clickedin"
                      className="w-full px-2 py-1.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20"
                    />
                  </td>
                  <td className="px-3 py-2">
                    <button
                      type="button"
                      onClick={() => removeSpec(idx)}
                      className="text-slate-300 hover:text-red-500 transition-colors"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </td>
                </tr>
              ))}
              {specs.length === 0 && (
                <tr>
                  <td colSpan={3} className="px-3 py-6 text-center text-slate-400 text-sm">
                    No specifications added yet
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <button
          type="button"
          onClick={addSpec}
          className="mt-3 flex items-center gap-1.5 text-sm text-orange-600 hover:text-orange-700 font-medium border border-orange-300 hover:border-orange-400 px-3 py-1.5 rounded-lg transition-all"
        >
          <Plus className="w-4 h-4" />
          Add Specification
        </button>
      </Section>

      {/* Measurements */}
      <Section title="Measurements">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200">
                <th className="px-3 py-2 text-left text-xs font-semibold text-slate-500 uppercase">Label</th>
                <th className="px-3 py-2 text-left text-xs font-semibold text-slate-500 uppercase">Value</th>
                <th className="px-3 py-2 w-10"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {measurements.map((item, idx) => (
                <tr key={idx} className="hover:bg-slate-50 transition-colors">
                  <td className="px-3 py-2">
                    <input type="text" value={item.name} onChange={e => updateMeasurement(idx, "name", e.target.value)} placeholder="e.g. Size" className="w-full px-2 py-1.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20" />
                  </td>
                  <td className="px-3 py-2">
                    <input type="text" value={item.value} onChange={e => updateMeasurement(idx, "value", e.target.value)} placeholder="e.g. 2.4 cm" className="w-full px-2 py-1.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20" />
                  </td>
                  <td className="px-3 py-2">
                    <button type="button" onClick={() => removeMeasurement(idx)} className="text-slate-300 hover:text-red-500 transition-colors"><Trash2 className="w-3.5 h-3.5" /></button>
                  </td>
                </tr>
              ))}
              {measurements.length === 0 && (
                <tr><td colSpan={3} className="px-3 py-6 text-center text-slate-400 text-sm">No measurements added yet</td></tr>
              )}
            </tbody>
          </table>
        </div>
        <button type="button" onClick={addMeasurement} className="mt-3 flex items-center gap-1.5 text-sm text-orange-600 hover:text-orange-700 font-medium border border-orange-300 hover:border-orange-400 px-3 py-1.5 rounded-lg transition-all">
          <Plus className="w-4 h-4" />Add Measurement
        </button>
      </Section>

      {/* Style Details */}
      <Section title="Style">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200">
                <th className="px-3 py-2 text-left text-xs font-semibold text-slate-500 uppercase">Label</th>
                <th className="px-3 py-2 text-left text-xs font-semibold text-slate-500 uppercase">Value</th>
                <th className="px-3 py-2 w-10"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {styleDetails.map((item, idx) => (
                <tr key={idx} className="hover:bg-slate-50 transition-colors">
                  <td className="px-3 py-2">
                    <input type="text" value={item.name} onChange={e => updateStyleDetail(idx, "name", e.target.value)} placeholder="e.g. Pattern" className="w-full px-2 py-1.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20" />
                  </td>
                  <td className="px-3 py-2">
                    <input type="text" value={item.value} onChange={e => updateStyleDetail(idx, "value", e.target.value)} placeholder="e.g. Solid" className="w-full px-2 py-1.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20" />
                  </td>
                  <td className="px-3 py-2">
                    <button type="button" onClick={() => removeStyleDetail(idx)} className="text-slate-300 hover:text-red-500 transition-colors"><Trash2 className="w-3.5 h-3.5" /></button>
                  </td>
                </tr>
              ))}
              {styleDetails.length === 0 && (
                <tr><td colSpan={3} className="px-3 py-6 text-center text-slate-400 text-sm">No style details added yet</td></tr>
              )}
            </tbody>
          </table>
        </div>
        <button type="button" onClick={addStyleDetail} className="mt-3 flex items-center gap-1.5 text-sm text-orange-600 hover:text-orange-700 font-medium border border-orange-300 hover:border-orange-400 px-3 py-1.5 rounded-lg transition-all">
          <Plus className="w-4 h-4" />Add Style Detail
        </button>
      </Section>

      {/* Trust Badges */}
      <Section title="Trust Badges (Feature Icons)">
        <div className="flex items-start gap-2 p-3 bg-blue-50 border border-blue-200 rounded-xl mb-4">
          <Info className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
          <p className="text-xs text-blue-700">
            <strong>Global defaults</strong> are set in <strong>Storefront Settings → Product Page Trust Badges</strong>. 
            Badges set here <strong>override</strong> the global defaults for this product only. 
            Leave empty to use the global defaults.
          </p>
        </div>
        <p className="text-xs text-slate-500 mb-4">Override badges for this product only. Toggle to enable/disable each badge.</p>
        <div className="space-y-3">
          {(data.trust_badges || DEFAULT_BADGES).map((badge, idx) => (
            <div key={idx} className="flex items-center gap-3 p-3 border border-slate-200 rounded-xl bg-slate-50/60">
              {/* Enable toggle */}
              <button
                type="button"
                onClick={() => {
                  const badges = data.trust_badges?.length ? data.trust_badges : DEFAULT_BADGES;
                  onChange("trust_badges", badges.map((b, i) => i === idx ? { ...b, enabled: !b.enabled } : b));
                }}
                className={`flex-shrink-0 w-10 h-5 rounded-full transition-all relative ${badge.enabled !== false ? "bg-orange-500" : "bg-slate-300"}`}
              >
                <span className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-all ${badge.enabled !== false ? "left-5" : "left-0.5"}`} />
              </button>
              {/* Icon selector */}
              <select
                value={badge.icon || "truck"}
                onChange={e => {
                  const badges = data.trust_badges?.length ? data.trust_badges : DEFAULT_BADGES;
                  onChange("trust_badges", badges.map((b, i) => i === idx ? { ...b, icon: e.target.value } : b));
                }}
                className="border border-slate-200 rounded-lg px-2 py-1.5 text-xs bg-white focus:outline-none focus:border-orange-500 flex-shrink-0"
              >
                {BADGE_ICON_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
              {/* Label input */}
              <input
                type="text"
                value={badge.label || ""}
                onChange={e => {
                  const badges = data.trust_badges?.length ? data.trust_badges : DEFAULT_BADGES;
                  onChange("trust_badges", badges.map((b, i) => i === idx ? { ...b, label: e.target.value } : b));
                }}
                placeholder="Badge label..."
                className="flex-1 px-2 py-1.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20 bg-white"
              />
              {/* Delete */}
              <button
                type="button"
                onClick={() => {
                  const badges = data.trust_badges?.length ? data.trust_badges : DEFAULT_BADGES;
                  onChange("trust_badges", badges.filter((_, i) => i !== idx));
                }}
                className="text-slate-300 hover:text-red-500 transition-colors flex-shrink-0"
              ><Trash2 className="w-3.5 h-3.5" /></button>
            </div>
          ))}
        </div>
        <button
          type="button"
          onClick={() => {
            const badges = data.trust_badges?.length ? [...data.trust_badges] : [...DEFAULT_BADGES];
            onChange("trust_badges", [...badges, { icon: "shield", label: "New Badge", enabled: true }]);
          }}
          className="mt-3 flex items-center gap-1.5 text-sm text-orange-600 hover:text-orange-700 font-medium border border-orange-300 hover:border-orange-400 px-3 py-1.5 rounded-lg transition-all"
        >
          <Plus className="w-4 h-4" />Add Badge
        </button>
      </Section>

      {/* Q&A */}
      <Section title="Product Q&amp;A">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200">
                <th className="px-3 py-2 text-left text-xs font-semibold text-slate-500 uppercase">Question</th>
                <th className="px-3 py-2 text-left text-xs font-semibold text-slate-500 uppercase">Answer</th>
                <th className="px-3 py-2 w-10"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {qa.map((item, idx) => (
                <tr key={idx} className="hover:bg-slate-50 transition-colors">
                  <td className="px-3 py-2">
                    <input
                      type="text"
                      value={item.question}
                      onChange={e => updateQA(idx, "question", e.target.value)}
                      placeholder="Enter question..."
                      className="w-full px-2 py-1.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20"
                    />
                  </td>
                  <td className="px-3 py-2">
                    <input
                      type="text"
                      value={item.answer}
                      onChange={e => updateQA(idx, "answer", e.target.value)}
                      placeholder="Enter answer..."
                      className="w-full px-2 py-1.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20"
                    />
                  </td>
                  <td className="px-3 py-2">
                    <button
                      type="button"
                      onClick={() => removeQA(idx)}
                      className="text-slate-300 hover:text-red-500 transition-colors"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </td>
                </tr>
              ))}
              {qa.length === 0 && (
                <tr>
                  <td colSpan={3} className="px-3 py-6 text-center text-slate-400 text-sm">
                    No Q&amp;A items added yet
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <button
          type="button"
          onClick={addQA}
          className="mt-3 flex items-center gap-1.5 text-sm text-orange-600 hover:text-orange-700 font-medium border border-orange-300 hover:border-orange-400 px-3 py-1.5 rounded-lg transition-all"
        >
          <Plus className="w-4 h-4" />
          Add Q&amp;A
        </button>
      </Section>
    </div>
  );
}
